import React, { useState, useRef, useEffect, useCallback } from "react";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import heic2any from "heic2any";
import { useAuth } from "../util/auth";
import { useRouter } from "../util/router";
import { db } from "../util/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText, // Added for confirmation dialog
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Divider,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  Snackbar, // Added for feedback
  List, // Added for tips
  ListItem, // Added for tips
  ListItemIcon, // Added for tips
  ListItemText, // Added for tips
  Modal, // Added for lightbox
  Backdrop, // Added for lightbox
  Fade, // Added for lightbox
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert"; // Added for feedback
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ZoomInIcon from "@material-ui/icons/ZoomIn"; // Added for lightbox trigger
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline"; // Added for tips
import GetAppIcon from "@material-ui/icons/GetApp";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import RefreshIcon from "@material-ui/icons/Refresh";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import ImageIcon from "@material-ui/icons/Image";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import { generatePortraits } from "./apiService";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(63, 81, 181, 0.08)"
        : "rgba(63, 81, 181, 0.04)",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(63, 81, 181, 0.12)"
          : "rgba(63, 81, 181, 0.08)",
      borderColor: theme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
    "&.dragging": {
      // Style for when dragging over
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(63, 81, 181, 0.15)"
          : "rgba(63, 81, 181, 0.1)",
      borderColor: theme.palette.primary.dark,
    },
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  fileInput: {
    display: "none",
  },
  previewImage: {
    width: "100%",
    maxHeight: "300px",
    objectFit: "contain",
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  portraitCard: {
    padding: theme.spacing(2),
    position: "relative",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
    },
  },
  portraitImage: {
    width: "100%",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  downloadButton: {
    marginTop: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    minHeight: "300px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  loadingText: {
    marginTop: theme.spacing(2),
    textAlign: "center",
  },
  portraitTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  portraitDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
  },
  galleryContainer: {
    marginTop: theme.spacing(3),
  },
  galleryTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  downloadAllButton: {
    marginLeft: theme.spacing(1),
  },
  originalImageContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  },
  originalImageTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  originalImage: {
    width: "100%",
    maxHeight: "200px",
    objectFit: "contain",
    borderRadius: theme.shape.borderRadius,
  },
  dividerWithText: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2, 0),
  },
  divider: {
    flexGrow: 1,
  },
  dividerText: {
    margin: theme.spacing(0, 2),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 3),
  },
  generateMoreButton: {
    marginRight: theme.spacing(2),
  },
  creditCost: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  creditIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1rem",
  },
  creditChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 500,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  insufficientCreditsMessage: {
    color: theme.palette.error.main,
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  // Lightbox styles
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    backgroundColor: theme.palette.background.paper,
    border: "none",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  modalImage: {
    maxWidth: "100%",
    maxHeight: "calc(90vh - 100px)", // Adjust based on padding/title
    objectFit: "contain",
    marginBottom: theme.spacing(2),
  },
  portraitImageClickable: {
    // Added style for clickable image
    cursor: "zoom-in",
  },
  downloadZipButton: {
    // Style for zip download button
    marginLeft: theme.spacing(1),
  },
}));

// Portrait style descriptions
const PORTRAIT_DESCRIPTIONS = [
  "LinkedIn-ready headshot with classic studio lighting",
  "Professional headshot with butterfly lighting and balanced illumination",
  "Corporate portrait with pleasing bokeh and butterfly lighting",
];

// Portrait style names
const PORTRAIT_TITLES = [
  "Classic Professional",
  "Modern Approachable",
  "Corporate Executive",
];

// Cost in credits for generating portraits
const PORTRAIT_GENERATION_COST = 3;

const ImageToPortraitDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = useRef(null);
  const auth = useAuth();
  const router = useRouter();

  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Covers generation and HEIC conversion
  const [loadingMessage, setLoadingMessage] = useState(""); // More specific loading text
  const [portraits, setPortraits] = useState([]);
  const [error, setError] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [isDragging, setIsDragging] = useState(false); // For drag-and-drop UI
  const [confirmOpen, setConfirmOpen] = useState(false); // Confirmation dialog state
  const [lightboxOpen, setLightboxOpen] = useState(false); // Lightbox state
  const [lightboxImage, setLightboxImage] = useState(""); // Lightbox image URL
  const [isZipping, setIsZipping] = useState(false); // State for zip download loading
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  }); // Snackbar feedback

  // Snackbar Alert component
  function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  }

  // Close snackbar handler
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch user credits from Firestore
  const fetchUserCredits = useCallback(async () => {
    if (auth.user) {
      const userDocRef = doc(db, "users", auth.user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        const credits = Number(data.credits) || 0;
        setUserCredits(credits);
        setInsufficientCredits(credits < PORTRAIT_GENERATION_COST);
      }
    }
  }, [auth.user]); // Added useCallback

  useEffect(() => {
    if (open) {
      fetchUserCredits();
    }
  }, [open, fetchUserCredits]); // Use useCallback dependency

  // Compress image using canvas
  const compressImage = (
    imageDataUrl,
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8
  ) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with specified quality
        const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedDataUrl);
      };
      img.src = imageDataUrl;
    });
  };

  // Process the selected file (used by both click and drag-drop)
  const processFile = async (file) => {
    if (!file) return;

    // Check file type
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/heic"];
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    const isHeic = fileType === "image/heic" || fileName.endsWith(".heic");
    const isValidExtension =
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png") ||
      isHeic;

    if (!isValidExtension) {
      setError("Please upload a JPEG, PNG, or HEIC image.");
      setSnackbar({
        open: true,
        message: "Invalid file type. Please use JPEG, PNG, or HEIC.",
        severity: "error",
      });
      return;
    }

    // Check file size (20MB max before conversion/compression)
    if (file.size > 20 * 1024 * 1024) {
      setError("File size should be less than 20MB.");
      setSnackbar({
        open: true,
        message: "File is too large (max 20MB).",
        severity: "error",
      });
      return;
    }

    setError(null);
    setIsLoading(true); // Show loading indicator during processing
    setLoadingMessage("Processing image..."); // Set specific message

    try {
      let processedFile = file;
      let targetMimeType = file.type;

      // Convert HEIC to JPEG
      if (isHeic) {
        setLoadingMessage("Converting HEIC image...");
        try {
          const conversionResult = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9, // Adjust quality as needed
          });
          processedFile = Array.isArray(conversionResult)
            ? conversionResult[0]
            : conversionResult; // heic2any might return array
          targetMimeType = "image/jpeg";
          setSnackbar({
            open: true,
            message: "HEIC image converted to JPEG.",
            severity: "info",
          });
        } catch (heicError) {
          console.error("HEIC Conversion Error:", heicError);
          setError(
            "Failed to convert HEIC image. Please try a different format."
          );
          setSnackbar({
            open: true,
            message: "HEIC conversion failed.",
            severity: "error",
          });
          setIsLoading(false);
          return;
        }
      }

      setUploadedImage(processedFile); // Store the potentially converted file

      // Create preview URL and compress if needed
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageDataUrl = e.target.result;
        let finalPreviewUrl = imageDataUrl;

        // Compress the image if it's large (check original size or converted size)
        if (processedFile.size > 1 * 1024 * 1024) {
          setLoadingMessage("Compressing image...");
          try {
            finalPreviewUrl = await compressImage(imageDataUrl);
            setSnackbar({
              open: true,
              message: "Image compressed for faster processing.",
              severity: "info",
            });
          } catch (compressErr) {
            console.error("Error compressing image:", compressErr);
            // Use original if compression fails, but notify user
            setSnackbar({
              open: true,
              message: "Could not compress image, using original.",
              severity: "warning",
            });
          }
        }
        setPreviewUrl(finalPreviewUrl);
        setIsLoading(false); // Processing finished
        setLoadingMessage("");
      };
      reader.onerror = () => {
        setError("Failed to read the image file.");
        setSnackbar({
          open: true,
          message: "Error reading file.",
          severity: "error",
        });
        setIsLoading(false);
        setLoadingMessage("");
      };
      reader.readAsDataURL(processedFile);
    } catch (processError) {
      console.error("File Processing Error:", processError);
      setError("An error occurred while processing the image.");
      setSnackbar({
        open: true,
        message: "Image processing failed.",
        severity: "error",
      });
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // Handle file selection via input click
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    processFile(file);
    // Reset file input to allow selecting the same file again
    if (event.target) {
      event.target.value = null;
    }
  };

  // Handle upload area click
  const handleUploadClick = () => {
    // Don't trigger click if loading
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  // Drag and Drop Handlers
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      if (isLoading) return; // Don't process if already loading

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        processFile(files[0]); // Process the first dropped file
        event.dataTransfer.clearData();
      }
    },
    [isLoading, processFile]
  ); // Include processFile in dependencies

  // Trigger confirmation dialog
  const triggerGenerateConfirmation = () => {
    if (!uploadedImage || isLoading || insufficientCredits) return;
    setConfirmOpen(true);
  };

  // Handle the actual generation after confirmation
  const handleConfirmGenerate = async () => {
    setConfirmOpen(false); // Close confirmation dialog
    if (!uploadedImage) return;

    // Check if user has enough credits
    if (userCredits < PORTRAIT_GENERATION_COST) {
      setInsufficientCredits(true);
      return;
    }

    setIsLoading(true);
    setLoadingMessage("Warming up the AI artist..."); // Initial loading message
    setError(null);

    try {
      // Convert image to base64 if it's not already
      const imageData = previewUrl;

      // Call the API
      // Add a slight delay for the initial message to show
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLoadingMessage("Generating your professional portraits..."); // Update message before API call

      const response = await generatePortraits(imageData);

      if (response.success) {
        setLoadingMessage("Finalizing portraits..."); // Update message after successful generation
        // Process the portraits
        setPortraits(
          response.portraits.map((portrait, index) => ({
            id: `portrait-${index}`,
            image: `data:image/jpeg;base64,${portrait.image}`,
            title: PORTRAIT_TITLES[index],
            description: PORTRAIT_DESCRIPTIONS[index],
          }))
        );

        // Deduct credits
        if (auth.user) {
          const userDocRef = doc(db, "users", auth.user.uid);
          const newCredits = userCredits - PORTRAIT_GENERATION_COST;
          await updateDoc(userDocRef, { credits: newCredits });
          setUserCredits(newCredits);
        }
      } else {
        setError("Failed to generate portraits. Please try again.");
      }
    } catch (error) {
      console.error("Error generating portraits:", error);
      setError(
        "An error occurred while generating portraits. Please try again."
      );
    } finally {
      setIsLoading(false);
      setLoadingMessage(""); // Clear loading message
    }
  };

  // Handle generate more portraits
  const handleGenerateMorePortraits = async () => {
    // Only clear the portraits, keep the uploaded image
    setPortraits([]);

    // Refetch user credits
    if (auth.user) {
      const userDocRef = doc(db, "users", auth.user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        const credits = Number(data.credits) || 0;
        setUserCredits(credits);
        setInsufficientCredits(credits < PORTRAIT_GENERATION_COST);
      }
    }
  };

  // Handle upgrade plan
  const handleUpgradePlan = () => {
    router.push("/pricing");
    handleDialogClose();
  };

  // Handle download portrait - improved for iOS compatibility
  const handleDownloadPortrait = (portrait, index) => {
    try {
      // Extract base64 data
      const imageData = portrait.image.split(",")[1];
      // Convert base64 to blob
      const byteCharacters = atob(imageData);
      const byteArrays = [];

      for (let i = 0; i < byteCharacters.length; i += 512) {
        const slice = byteCharacters.slice(i, i + 512);
        const byteNumbers = new Array(slice.length);

        for (let j = 0; j < slice.length; j++) {
          byteNumbers[j] = slice.charCodeAt(j);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
      }

      const blob = new Blob(byteArrays, { type: "image/jpeg" });

      // Use FileSaver for better cross-platform compatibility
      saveAs(
        blob,
        `professional-portrait-${portrait.title
          .toLowerCase()
          .replace(/\s+/g, "-")}.jpg`
      );
    } catch (error) {
      console.error("Error downloading portrait:", error);
      // Fallback to traditional method if FileSaver fails
      const link = document.createElement("a");
      link.href = portrait.image;
      link.download = `professional-portrait-${portrait.title
        .toLowerCase()
        .replace(/\s+/g, "-")}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle download all portraits as ZIP
  const handleDownloadAllPortraitsZip = async () => {
    if (isZipping) return; // Prevent multiple clicks

    setIsZipping(true);
    setSnackbar({
      open: true,
      message: "Creating ZIP file...",
      severity: "info",
    });

    const zip = new JSZip();
    const imagePromises = portraits.map(async (portrait) => {
      const filename = `professional-portrait-${portrait.title
        .toLowerCase()
        .replace(/\s+/g, "-")}.jpg`;
      try {
        const response = await fetch(portrait.image);
        const blob = await response.blob();
        zip.file(filename, blob);
      } catch (error) {
        console.error(`Error fetching/adding ${filename} to zip:`, error);
        // Optionally add a placeholder or skip the file
      }
    });

    try {
      await Promise.all(imagePromises);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "professional-portraits.zip");
      setSnackbar({
        open: true,
        message: "Portraits ZIP downloaded.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error generating ZIP file:", error);
      setSnackbar({
        open: true,
        message: "Failed to create ZIP file.",
        severity: "error",
      });
    } finally {
      setIsZipping(false);
    }
  };

  // Lightbox handlers
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Reset state when dialog closes
  const handleDialogClose = () => {
    if (!isLoading) {
      setUploadedImage(null);
      setPreviewUrl(null);
      setPortraits([]);
      setError(null);
      // Reset drag state, confirmation, and lightbox on close
      setIsDragging(false);
      setConfirmOpen(false);
      setLightboxOpen(false); // Close lightbox if open
      setIsZipping(false); // Reset zip loading state
      onClose();
    }
  };

  return (
    <>
      {" "}
      {/* Added Fragment for Snackbar */}
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">Professional Portrait Generator</Typography>
          <IconButton
            className={classes.closeButton}
            onClick={handleDialogClose}
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {!portraits.length && !isLoading && (
            <>
              <Typography variant="body1" gutterBottom>
                Upload a photo of yourself to generate three professional
                portrait variations.
              </Typography>

              {/* Credit cost indicator */}
              <Box className={classes.creditCost}>
                <AccountBalanceWalletIcon className={classes.creditIcon} />
                <Typography variant="body2" style={{ marginRight: 8 }}>
                  Cost:
                </Typography>
                <Chip
                  label={`${PORTRAIT_GENERATION_COST} Credits`}
                  size="small"
                  className={classes.creditChip}
                />
              </Box>

              <Box mt={2} mb={3}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg, image/png, image/jpg, image/heic"
                  className={classes.fileInput}
                  onChange={handleFileChange}
                  disabled={isLoading} // Disable input while loading
                />

                <div
                  className={`${classes.uploadArea} ${
                    isDragging ? "dragging" : ""
                  }`}
                  onClick={handleUploadClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <PhotoCameraIcon className={classes.uploadIcon} />
                  <Typography variant="h6" gutterBottom>
                    {isDragging
                      ? "Drop image here"
                      : "Click or Drag & Drop Photo"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    JPEG, PNG, or HEIC - Large photos will be automatically
                    compressed
                  </Typography>
                </div>
              </Box>

              {error && (
                <Typography variant="body2" color="error" gutterBottom>
                  {error}
                </Typography>
              )}

              {insufficientCredits && (
                <Typography
                  variant="body2"
                  className={classes.insufficientCreditsMessage}
                >
                  You don't have enough credits. This feature requires{" "}
                  {PORTRAIT_GENERATION_COST} credits.
                </Typography>
              )}

              {previewUrl && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Preview:
                  </Typography>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className={classes.previewImage}
                  />
                </Box>
              )}

              {/* Upload Tips */}
              {!previewUrl && !isLoading && (
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tips for a good photo:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon
                          fontSize="small"
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText primary="Clear, well-lit face" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon
                          fontSize="small"
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText primary="Looking towards the camera" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon
                          fontSize="small"
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText primary="Simple background preferred" />
                    </ListItem>
                  </List>
                </Box>
              )}
            </>
          )}

          {isLoading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="h6" className={classes.loadingText}>
                {loadingMessage || "Generating your professional portraits..."}{" "}
                {/* Use dynamic message */}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                {loadingMessage.includes("Generating")
                  ? "This may take up to a minute. We're creating three unique professional portraits."
                  : "Please wait..."}{" "}
                {/* Adjust subtext */}
                professional portraits.
              </Typography>
              {previewUrl && (
                <Box mt={3} textAlign="center" maxWidth="200px">
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    gutterBottom
                  >
                    Using this photo:
                  </Typography>
                  <img
                    src={previewUrl}
                    alt="Original"
                    style={{
                      width: "100%",
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      marginTop: theme.spacing(1),
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {portraits.length > 0 && !isLoading && (
            <Box className={classes.galleryContainer}>
              {/* Original image display */}
              <Paper elevation={1} className={classes.originalImageContainer}>
                <Box className={classes.originalImageTitle}>
                  <ImageIcon
                    style={{
                      marginRight: 8,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                    Original Photo
                  </Typography>
                </Box>
                <img
                  src={previewUrl}
                  alt="Original"
                  className={classes.originalImage}
                />
              </Paper>

              <div className={classes.dividerWithText}>
                <Divider className={classes.divider} />
                <Typography variant="body2" className={classes.dividerText}>
                  <CompareArrowsIcon
                    style={{
                      fontSize: 16,
                      verticalAlign: "middle",
                      marginRight: 4,
                    }}
                  />
                  TRANSFORMED INTO
                </Typography>
                <Divider className={classes.divider} />
              </div>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" className={classes.galleryTitle}>
                  Your Professional Portraits
                </Typography>
                <Tooltip title="Download all portraits as a ZIP file">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={
                      isZipping ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <GetAppIcon />
                      )
                    }
                    onClick={handleDownloadAllPortraitsZip}
                    className={classes.downloadZipButton} // Use new class
                    size="small"
                    disabled={isZipping}
                  >
                    {isZipping ? "Zipping..." : "Download All (ZIP)"}
                  </Button>
                </Tooltip>
              </Box>

              <Grid container spacing={3}>
                {portraits.map((portrait, index) => (
                  <Grid item xs={12} sm={4} key={portrait.id}>
                    <Paper elevation={2} className={classes.portraitCard}>
                      <Tooltip title="Click to enlarge">
                        <img
                          src={portrait.image}
                          alt={`Portrait ${index + 1}`}
                          className={`${classes.portraitImage} ${classes.portraitImageClickable}`} // Added clickable class
                          onClick={() => handleOpenLightbox(portrait.image)} // Added onClick
                        />
                      </Tooltip>
                      <Typography
                        variant="subtitle1"
                        className={classes.portraitTitle}
                      >
                        {portrait.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.portraitDescription}
                      >
                        {portrait.description}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<GetAppIcon />}
                        fullWidth
                        onClick={() => handleDownloadPortrait(portrait, index)}
                        className={classes.downloadButton}
                      >
                        Download
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          {!portraits.length && !isLoading && !insufficientCredits && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={triggerGenerateConfirmation} // Changed to trigger confirmation
              disabled={!uploadedImage || isLoading || insufficientCredits} // Added insufficientCredits check here too
            >
              Generate Portraits ({PORTRAIT_GENERATION_COST} Credits)
            </Button>
          )}

          {!portraits.length && !isLoading && insufficientCredits && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpgradePlan}
            >
              Upgrade Plan
            </Button>
          )}

          {portraits.length > 0 && !isLoading && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleGenerateMorePortraits}
                className={classes.generateMoreButton}
                disabled={userCredits < PORTRAIT_GENERATION_COST}
              >
                Generate More
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDialogClose}
              >
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Portrait Generation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Generating portraits will use{" "}
            <strong>{PORTRAIT_GENERATION_COST} credits</strong>. Are you sure
            you want to proceed with the selected image?
          </DialogContentText>
          {previewUrl && (
            <Box mt={2} textAlign="center">
              <img
                src={previewUrl}
                alt="Confirm Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "150px",
                  borderRadius: theme.shape.borderRadius,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGenerate}
            color="primary"
            variant="contained"
          >
            Confirm & Generate
          </Button>
        </DialogActions>
      </Dialog>
      {/* Lightbox Modal */}
      <Modal
        aria-labelledby="portrait-lightbox-title"
        aria-describedby="portrait-lightbox-description"
        className={classes.modal}
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={lightboxOpen}>
          <div className={classes.modalContent}>
            <IconButton
              aria-label="close"
              onClick={handleCloseLightbox}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={lightboxImage}
              alt="Enlarged Portrait"
              className={classes.modalImage}
            />
            {/* Optional: Add title or download button here too */}
          </div>
        </Fade>
      </Modal>
    </> // Close Fragment
  );
};

export default ImageToPortraitDialog;
